import { useEffect, useState, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useSitesStore } from '@/stores/sites';
import { useAnalyticsStore } from '@/stores/analytics';
import { usePreferencesStore } from '@/stores/preferences';
import { analyticsService } from '@/services/analytics';
import { Card, CardHeader, Button } from '@/components/UI';
import {
  MetricCard,
  AnalyticsChart,
  TopPages,
  Sources,
  PeriodSelector,
  StatsList,
  FilterBar,
} from '@/components/Dashboard';
import { getCountryName } from '@/utils/countries';
import type { FilterKey, TopPage, EntryPage, ExitPage, TopReferrer, ChannelData, CampaignData } from '@/types';

// Format seconds into human-readable duration
function formatDuration(seconds: number): string {
  if (seconds === 0) return '--';
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  if (minutes < 60) {
    return remainingSeconds > 0 ? `${minutes}m ${remainingSeconds}s` : `${minutes}m`;
  }
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  return remainingMinutes > 0 ? `${hours}h ${remainingMinutes}m` : `${hours}h`;
}

type LocationTab = 'countries' | 'regions' | 'cities';
type TechTab = 'browsers' | 'os' | 'devices' | 'crawlers' | 'bots';

const TECH_TAB_LABELS: Record<TechTab, string> = {
  browsers: 'Browsers',
  os: 'OS',
  devices: 'Devices',
  crawlers: 'Crawlers',
  bots: 'Bots',
};

export function SiteDashboard() {
  const { siteId } = useParams<{ siteId: string }>();
  const { currentSite, fetchSite } = useSitesStore();
  const { timezone, fetchPreferences } = usePreferencesStore();
  const {
    siteAnalytics,
    timeSeries,
    comparison,
    realtimeVisitors,
    realtimeCrawlers,
    realtimeBots,
    period,
    customDateRange,
    filters,
    setPeriod,
    setCustomDateRange,
    setFilter,
    removeFilter,
    clearFilters,
    clearAnalytics,
    fetchSiteAnalytics,
    fetchRealtimeVisitors,
    isLoading,
  } = useAnalyticsStore();

  const [locationTab, setLocationTab] = useState<LocationTab>('countries');
  const [techTab, setTechTab] = useState<TechTab>('browsers');

  // Extended data from "show more" requests
  const [extendedPages, setExtendedPages] = useState<TopPage[]>([]);
  const [extendedEntryPages, setExtendedEntryPages] = useState<EntryPage[]>([]);
  const [extendedExitPages, setExtendedExitPages] = useState<ExitPage[]>([]);
  const [extendedReferrers, setExtendedReferrers] = useState<TopReferrer[]>([]);
  const [extendedChannels, setExtendedChannels] = useState<ChannelData[]>([]);
  const [extendedCampaigns, setExtendedCampaigns] = useState<CampaignData[]>([]);

  // Reset extended data when analytics refresh
  useEffect(() => {
    setExtendedPages([]);
    setExtendedEntryPages([]);
    setExtendedExitPages([]);
    setExtendedReferrers([]);
    setExtendedChannels([]);
    setExtendedCampaigns([]);
  }, [siteAnalytics]);

  useEffect(() => {
    fetchPreferences();
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [siteId]);

  useEffect(() => {
    if (siteId) {
      clearAnalytics();
      clearFilters();
      fetchSite(siteId);
      fetchSiteAnalytics(siteId, period);
      fetchRealtimeVisitors(siteId);

      // Refresh realtime every 30 seconds
      const realtimeInterval = setInterval(() => {
        fetchRealtimeVisitors(siteId);
      }, 30000);

      // Refresh full analytics every 60 seconds
      const analyticsInterval = setInterval(() => {
        fetchSiteAnalytics(siteId, period);
      }, 60000);

      return () => {
        clearInterval(realtimeInterval);
        clearInterval(analyticsInterval);
      };
    }
  }, [siteId, period, customDateRange]);

  const handleFilter = useCallback((key: FilterKey, value: string) => {
    if (!siteId) return;
    setFilter(key, value);
    fetchSiteAnalytics(siteId, period);
  }, [siteId, period, setFilter, fetchSiteAnalytics]);

  const handleRemoveFilter = useCallback((key: FilterKey) => {
    removeFilter(key);
    if (siteId) fetchSiteAnalytics(siteId, period);
  }, [siteId, period, removeFilter, fetchSiteAnalytics]);

  const handleClearFilters = useCallback(() => {
    clearFilters();
    if (siteId) fetchSiteAnalytics(siteId, period);
  }, [siteId, period, clearFilters, fetchSiteAnalytics]);

  const handleShowMorePages = useCallback(async (tab: string) => {
    if (!siteId) return;
    const dimension = tab === 'entry' ? 'entry_page' : tab === 'exit' ? 'exit_page' : 'page_path';
    const currentItems = tab === 'entry' ? (siteAnalytics?.entry_pages || []).length + extendedEntryPages.length
      : tab === 'exit' ? (siteAnalytics?.exit_pages || []).length + extendedExitPages.length
      : (siteAnalytics?.top_pages || []).length + extendedPages.length;

    const response = await analyticsService.getBreakdown(
      siteId, dimension, 10, currentItems, period, customDateRange, filters,
    );
    if (tab === 'entry') {
      setExtendedEntryPages(prev => [...prev, ...response.data as unknown as EntryPage[]]);
    } else if (tab === 'exit') {
      setExtendedExitPages(prev => [...prev, ...response.data as unknown as ExitPage[]]);
    } else {
      setExtendedPages(prev => [...prev, ...response.data as unknown as TopPage[]]);
    }
  }, [siteId, period, customDateRange, filters, siteAnalytics, extendedPages, extendedEntryPages, extendedExitPages]);

  const handleShowMoreSources = useCallback(async (tab: string) => {
    if (!siteId) return;
    const dimension = tab === 'channels' ? 'channel' : tab === 'campaigns' ? 'utm_campaign' : 'referrer_host';
    const currentItems = tab === 'channels' ? (siteAnalytics?.channels || []).length + extendedChannels.length
      : tab === 'campaigns' ? (siteAnalytics?.campaigns || []).length + extendedCampaigns.length
      : (siteAnalytics?.top_referrers || []).length + extendedReferrers.length;

    const response = await analyticsService.getBreakdown(
      siteId, dimension, 10, currentItems, period, customDateRange, filters,
    );
    if (tab === 'channels') {
      setExtendedChannels(prev => [...prev, ...response.data as unknown as ChannelData[]]);
    } else if (tab === 'campaigns') {
      setExtendedCampaigns(prev => [...prev, ...response.data as unknown as CampaignData[]]);
    } else {
      setExtendedReferrers(prev => [...prev, ...response.data as unknown as TopReferrer[]]);
    }
  }, [siteId, period, customDateRange, filters, siteAnalytics, extendedChannels, extendedCampaigns, extendedReferrers]);

  if (!currentSite && !isLoading) {
    return (
      <div className="text-center py-12">
        <h2 className="text-xl font-semibold text-gray-900">Site not found</h2>
        <Link to="/dashboard" className="mt-4 inline-block">
          <Button variant="secondary">Back to dashboard</Button>
        </Link>
      </div>
    );
  }

  // Merge base data with extended "show more" data
  const allPages = [...(siteAnalytics?.top_pages || []), ...extendedPages];
  const allEntryPages = [...(siteAnalytics?.entry_pages || []), ...extendedEntryPages];
  const allExitPages = [...(siteAnalytics?.exit_pages || []), ...extendedExitPages];
  const allReferrers = [...(siteAnalytics?.top_referrers || []), ...extendedReferrers];
  const allChannels = [...(siteAnalytics?.channels || []), ...extendedChannels];
  const allCampaigns = [...(siteAnalytics?.campaigns || []), ...extendedCampaigns];

  // Location tab items
  const locationItems = locationTab === 'countries'
    ? (siteAnalytics?.countries || []).map(c => ({ label: getCountryName(c.code), count: c.count }))
    : locationTab === 'regions'
    ? (siteAnalytics?.regions || []).map(r => ({ label: r.name, count: r.count }))
    : (siteAnalytics?.cities || []).map(c => ({ label: c.name, count: c.count }));

  const locationFilterKey: FilterKey = locationTab === 'countries' ? 'country' : locationTab === 'regions' ? 'region' : 'city';

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-3">
          <Link to="/dashboard" className="text-gray-500 hover:text-gray-700">
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </Link>
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-2xl font-bold text-gray-900">
                {currentSite?.name || 'Loading...'}
              </h1>
              {currentSite && (
                <div className="w-8 h-8 rounded-lg bg-white border border-gray-200 flex items-center justify-center flex-shrink-0 overflow-hidden">
                  <img
                    src={`https://www.google.com/s2/favicons?domain=${currentSite.domain}&sz=64`}
                    alt=""
                    className="w-5 h-5"
                    onError={(e) => {
                      e.currentTarget.style.display = 'none';
                      (e.currentTarget.nextElementSibling as HTMLElement)?.classList.remove('hidden');
                    }}
                  />
                  <svg className="w-4 h-4 text-gray-400 hidden" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
                  </svg>
                </div>
              )}
            </div>
            <div className="flex items-center gap-2">
              <p className="text-gray-600">{currentSite?.domain}</p>
              {currentSite && (
                <div className="relative group">
                  <span
                    className={`inline-flex h-2 w-2 rounded-full ${
                      currentSite.beacon_installed === null
                        ? 'bg-gray-400'
                        : currentSite.beacon_installed
                        ? 'bg-green-500'
                        : 'bg-red-500'
                    }`}
                  />
                  <div className="absolute left-full top-1/2 -translate-y-1/2 ml-1.5 px-2 py-1 text-xs font-medium text-white bg-gray-900 rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                    {currentSite.beacon_installed === null
                      ? 'Checking beacon status...'
                      : currentSite.beacon_installed
                      ? 'Beacon installed'
                      : 'Beacon not installed'}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2 text-sm">
            <span className="inline-flex h-2 w-2 rounded-full bg-green-500 animate-pulse" />
            <span className="text-gray-600">{realtimeVisitors} online now</span>
            {realtimeCrawlers > 0 && (
              <span
                className="text-gray-400"
                title="Named crawlers (search engines, SEO and AI crawlers, link previews, monitors) in the last 5 minutes (excluded from stats)"
              >
                + {realtimeCrawlers} {realtimeCrawlers === 1 ? 'crawler' : 'crawlers'}
              </span>
            )}
            {realtimeBots > 0 && (
              <span
                className="text-gray-400"
                title="Other automated traffic (HTTP libraries, headless browsers, scanners, unidentified bots) in the last 5 minutes (excluded from stats)"
              >
                + {realtimeBots} {realtimeBots === 1 ? 'bot' : 'bots'}
              </span>
            )}
          </div>
          <PeriodSelector
            value={period}
            onChange={setPeriod}
            customDateRange={customDateRange}
            onCustomDateRange={setCustomDateRange}
          />
          <Link to={`/sites/${siteId}/events`}>
            <Button variant="secondary" size="sm">
              <svg className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
              </svg>
              Event Log
            </Button>
          </Link>
          <Link to={`/sites/${siteId}/settings`}>
            <Button variant="secondary" size="sm">
              <svg className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              Settings
            </Button>
          </Link>
        </div>
      </div>

      {/* Filter Bar */}
      <FilterBar filters={filters} onRemove={handleRemoveFilter} onClear={handleClearFilters} />

      {/* Metrics */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
        <MetricCard
          title="Pageviews"
          value={siteAnalytics?.total_pageviews || 0}
          change={comparison?.pageviews_change}
        />
        <MetricCard
          title="Unique Visitors"
          value={siteAnalytics?.unique_visitors || 0}
          change={comparison?.visitors_change}
        />
        <MetricCard
          title="Views / Visit"
          value={siteAnalytics?.views_per_visit != null ? siteAnalytics.views_per_visit : 0}
          change={comparison?.views_per_visit_change}
        />
        <MetricCard
          title="Bounce Rate"
          value={siteAnalytics?.bounce_rate != null ? `${siteAnalytics.bounce_rate}%` : '--'}
        />
        <MetricCard
          title="Avg. Time on Site"
          value={formatDuration(siteAnalytics?.avg_time_on_site || 0)}
        />
      </div>

      {/* Chart */}
      <Card>
        <CardHeader title="Traffic Overview" />
        <AnalyticsChart data={timeSeries} timezone={timezone} isLoading={isLoading} />
      </Card>

      {/* Pages & Sources */}
      <div className="grid lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader title="Pages & Events" />
          <TopPages
            pages={allPages}
            entryPages={allEntryPages}
            exitPages={allExitPages}
            customEvents={siteAnalytics?.custom_events || []}
            isLoading={isLoading}
            onFilter={handleFilter}
            onShowMore={handleShowMorePages}
            hasMore
          />
        </Card>

        <Card>
          <CardHeader title="Sources" />
          <Sources
            referrers={allReferrers}
            channels={allChannels}
            campaigns={allCampaigns}
            isLoading={isLoading}
            onFilter={handleFilter}
            onShowMore={handleShowMoreSources}
            hasMore
          />
        </Card>
      </div>

      {/* Additional Stats */}
      <div className="grid lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader title="Technology" />
          <div className="flex gap-1 mb-4">
            {(Object.keys(TECH_TAB_LABELS) as TechTab[]).map((tab) => (
              <button
                key={tab}
                onClick={() => setTechTab(tab)}
                className={`px-2 py-1 text-xs font-medium rounded transition-colors ${
                  techTab === tab
                    ? 'bg-primary-50 text-primary-700'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                {TECH_TAB_LABELS[tab]}
              </button>
            ))}
          </div>
          <StatsList
            items={techTab === 'browsers'
              ? (siteAnalytics?.browsers || []).map((b) => ({
                  label: b.name || 'Unknown',
                  count: b.count,
                }))
              : techTab === 'os'
              ? (siteAnalytics?.operating_systems || []).map((o) => ({
                  label: o.name || 'Unknown',
                  count: o.count,
                }))
              : techTab === 'crawlers' || techTab === 'bots'
              ? (siteAnalytics?.[techTab] || []).map((b) => ({
                  label: b.name,
                  count: b.count,
                }))
              : (siteAnalytics?.devices || []).map((d) => ({
                  label: d.type ? d.type.charAt(0).toUpperCase() + d.type.slice(1) : 'Unknown',
                  count: d.count,
                }))
            }
            color="#8b5cf6"
            isLoading={isLoading}
            filterKey={techTab === 'browsers'
              ? 'browser'
              : techTab === 'os'
              ? 'os'
              : techTab === 'devices'
              ? 'device_type'
              : undefined
            }
            onFilter={handleFilter}
            hasMore={techTab === 'browsers'
              ? (siteAnalytics?.browsers || []).length >= 10
              : techTab === 'os'
              ? (siteAnalytics?.operating_systems || []).length >= 10
              : false
            }
          />
        </Card>

        <Card>
          <CardHeader title="Location" />
          <div className="flex gap-1 mb-4">
            {(['countries', 'regions', 'cities'] as LocationTab[]).map((tab) => (
              <button
                key={tab}
                onClick={() => setLocationTab(tab)}
                className={`px-2 py-1 text-xs font-medium rounded transition-colors ${
                  locationTab === tab
                    ? 'bg-primary-50 text-primary-700'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                {tab.charAt(0).toUpperCase() + tab.slice(1)}
              </button>
            ))}
          </div>
          <StatsList
            items={locationItems}
            color="#f59e0b"
            isLoading={isLoading}
            filterKey={locationFilterKey}
            onFilter={handleFilter}
            hasMore={locationItems.length >= 10}
          />
        </Card>
      </div>
    </div>
  );
}

export default SiteDashboard;
